import { ColumnType, SortDirection, Trade } from './types';
import { getSectorShortName } from './sectors';
import { getOptionFlowReadLabel } from './getOptionFlowSideIntelligence';
import { getTradeExecutionMs } from 'utils/shared/trades/getOptionFlowTimestampMs';

export interface OptionFlowSortHeader {
  sortKey: string;
  columnType: ColumnType;
}

export const getNextOptionFlowSortState = ({
  currentSortKey,
  currentSortDirection,
  selectedSortKey,
  defaultSortKey,
}: {
  currentSortKey: string;
  currentSortDirection: SortDirection;
  selectedSortKey: string;
  defaultSortKey: string;
}) => {
  if (currentSortKey !== selectedSortKey || currentSortDirection === SortDirection.None) {
    return {
      sortKey: selectedSortKey,
      sortDirection: SortDirection.Desc,
    };
  }

  if (currentSortDirection === SortDirection.Desc) {
    return {
      sortKey: selectedSortKey,
      sortDirection: SortDirection.Asc,
    };
  }

  return {
    sortKey: defaultSortKey,
    sortDirection: SortDirection.None,
  };
};

type SortableTrade = Trade & {
  heatScore?: number;
  scoreWidth?: number;
  type?: string | null;
  createdAt?: string | null;
};

const parseFiniteTimestamp = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const timestamp = Date.parse(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  return null;
};

const parseDatePartsTimestamp = (
  yearValue: unknown,
  monthValue: unknown,
  dayValue: unknown,
): number | null => {
  const year = Number(yearValue);
  const month = Number(monthValue);
  const day = Number(dayValue);

  if (
    !Number.isInteger(year) ||
    !Number.isInteger(month) ||
    !Number.isInteger(day) ||
    month < 1 ||
    month > 12 ||
    day < 1
  ) {
    return null;
  }

  const timestamp = Date.UTC(year, month - 1, day);
  const parsedDate = new Date(timestamp);

  if (
    parsedDate.getUTCFullYear() !== year ||
    parsedDate.getUTCMonth() !== month - 1 ||
    parsedDate.getUTCDate() !== day
  ) {
    return null;
  }

  return timestamp;
};

const parseDateStringTimestamp = (value?: string | null): number | null => {
  const match = value?.trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (!match) {
    return null;
  }

  return parseDatePartsTimestamp(match[1], match[2], match[3]);
};

const getExpiryTimestamp = (trade: SortableTrade): number | null => {
  if (typeof trade.expiry === 'number' && Number.isFinite(trade.expiry)) {
    return trade.expiry;
  }

  const fromExpiryParts = parseDatePartsTimestamp(
    trade.expireYear,
    trade.expireMonth,
    trade.expireDay,
  );
  if (fromExpiryParts != null) {
    return fromExpiryParts;
  }

  const fromExpiryStringParts = parseDateStringTimestamp(trade.expiryString);
  if (fromExpiryStringParts != null) {
    return fromExpiryStringParts;
  }

  const fromExpiryString = parseFiniteTimestamp(trade.expiryString);
  if (fromExpiryString != null) {
    return fromExpiryString;
  }

  return null;
};

const getDateSortValue = (trade: SortableTrade, sortKey: string): number | null => {
  if (sortKey === 'expiry') {
    return getExpiryTimestamp(trade);
  }

  if (sortKey === 'createdAt') {
    // Keep the saved Time sort key while sorting by execution, not arrival.
    return getTradeExecutionMs(trade) ?? null;
  }

  return parseFiniteTimestamp((trade as Record<string, unknown>)[sortKey]);
};

const getNumberSortValue = (trade: SortableTrade, sortKey: string): number | null => {
  const rawValue =
    sortKey === 'heatScore'
      ? trade.heatScore ?? trade.scoreWidth
      : (trade as Record<string, unknown>)[sortKey];
  const numberValue = typeof rawValue === 'string' ? Number(rawValue) : rawValue;
  return typeof numberValue === 'number' && Number.isFinite(numberValue) ? numberValue : null;
};

const getTextSortValue = (trade: SortableTrade, sortKey: string): string | null => {
  const rawValue =
    sortKey === 'sector'
      ? getSectorShortName(trade.tickerDetails?.sector || '')
      : sortKey === 'sideRead'
      ? getOptionFlowReadLabel(trade)
      : sortKey === 'type'
      ? trade.type ?? trade.orderType
      : (trade as Record<string, unknown>)[sortKey];

  if (rawValue == null) {
    return null;
  }

  const textValue = String(rawValue).trim();
  return textValue ? textValue.toLocaleLowerCase() : null;
};

const comparePresentValues = (first: string | number, last: string | number): number => {
  if (typeof first === 'string' || typeof last === 'string') {
    return String(first).localeCompare(String(last));
  }

  return first - last;
};

export const sortOptionFlowTrades = <T extends SortableTrade>({
  trades,
  headers,
  sortKey,
  sortDirection,
  defaultSortKey,
}: {
  trades: T[];
  headers: OptionFlowSortHeader[];
  sortKey: string;
  sortDirection: SortDirection;
  defaultSortKey: string;
}): T[] => {
  const activeHeader =
    sortDirection === SortDirection.None
      ? headers.find((header) => header.sortKey === defaultSortKey)
      : headers.find((header) => header.sortKey === sortKey) ??
        headers.find((header) => header.sortKey === defaultSortKey);

  if (!activeHeader) {
    return [...trades];
  }

  const activeSortKey = activeHeader.sortKey;
  const direction = sortDirection === SortDirection.Asc ? 1 : -1;

  return trades
    .map((trade, index) => ({ trade, index }))
    .sort((a, b) => {
      const getValue = (trade: T) => {
        if (activeHeader.columnType === ColumnType.Date) {
          return getDateSortValue(trade, activeSortKey);
        }

        if (activeHeader.columnType === ColumnType.Number) {
          return getNumberSortValue(trade, activeSortKey);
        }

        return getTextSortValue(trade, activeSortKey);
      };

      const firstValue = getValue(a.trade);
      const lastValue = getValue(b.trade);

      if (firstValue == null && lastValue != null) {
        return 1;
      }

      if (firstValue != null && lastValue == null) {
        return -1;
      }

      const result =
        firstValue == null || lastValue == null ? 0 : comparePresentValues(firstValue, lastValue);
      if (result !== 0) {
        return result * direction;
      }

      const firstCreatedAt = getDateSortValue(a.trade, 'createdAt');
      const lastCreatedAt = getDateSortValue(b.trade, 'createdAt');
      const createdAtTieBreak =
        firstCreatedAt == null || lastCreatedAt == null
          ? 0
          : comparePresentValues(firstCreatedAt, lastCreatedAt) * -1;

      if (createdAtTieBreak !== 0) {
        return createdAtTieBreak;
      }

      return a.index - b.index;
    })
    .map(({ trade }) => trade);
};
